import { useEffect, useState, useSyncExternalStore } from 'react'

/** Below this the bar is always out: at the top of a page there is nothing to get out of. */
const SETTLED = 80

/** Ignore a wobble. A trackpad reports a few pixels either way while a finger rests on it. */
const MEANT_IT = 6

/**
 * 720px, the number the calendar's renderers branch on. Asked live rather than once: a tablet
 * turned on its side changes the answer while the page is open.
 */
const PHONE = '(max-width: 720px)'

function subscribePhone(onChange: () => void): () => void {
  const query = window.matchMedia?.(PHONE)
  if (!query) return () => {}
  query.addEventListener('change', onChange)
  return () => query.removeEventListener('change', onChange)
}

function readPhone(): boolean {
  return typeof window !== 'undefined' && window.matchMedia?.(PHONE).matches === true
}

/**
 * Whether the bar should be off the screen.
 *
 * Reading is downwards, so the bar goes when you are going that way and comes back the
 * moment you turn round — which is when you are looking for it. The alternative, and what
 * this had, is a bar that scrolls away like any other element and is reachable only by
 * returning to the top of the page.
 *
 * Direction rather than position, because position cannot tell "I have read this far" from
 * "I want the search box".
 *
 * **Never on a phone.** Both bars ask this one hook, and on a phone it answers `false`
 * whatever the scroll does. Two things follow. The masthead's measured height is then a
 * constant for the life of the page, so the toolbar's `top` never transitions — the toolbar
 * travelled in two properties on two clocks (its own transform, and a `top` that followed a
 * height published a render later), and on a phone that ran alongside the browser folding
 * its own address bar, which is a bar that looks as if it is floating. And on a phone the
 * top bars are the paging control and the tab switch, and a thumb reaches the top of a
 * screen less easily than a pointer reaches the top of a window. What the browser does to a
 * sticky bar while its chrome moves is the browser's, and is left to it.
 */
export function useBarsHidden(): boolean {
  const [hidden, setHidden] = useState(false)
  const phone = useSyncExternalStore(subscribePhone, readPhone, () => false)

  useEffect(() => {
    // No listener at all on a phone, rather than a listener whose answer is discarded: a
    // scroll handler that sets state the bar never reads is a re-render per scroll frame for
    // nothing. Back at `false` in case the width changed with the bar away.
    if (phone) {
      setHidden(false)
      return
    }
    let last = window.scrollY
    let frame = 0
    let waiting = false
    const read = () => {
      waiting = false
      const now = window.scrollY
      const moved = now - last
      if (Math.abs(moved) < MEANT_IT) return
      last = now
      setHidden(now > SETTLED && moved > 0)
    }
    // Coalesced to a frame: a scroll fires far more often than the screen redraws, and this
    // decides one class.
    const onScroll = () => {
      if (waiting) return
      // Claimed before the frame is asked for, not after. `frame ||= rAF(read)` reads as the
      // same thing and is not: a frame that runs before the assignment lands leaves the id
      // written over the zero `read` just wrote, and nothing is ever scheduled again.
      waiting = true
      frame = window.requestAnimationFrame(read)
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      window.removeEventListener('scroll', onScroll)
      if (frame) window.cancelAnimationFrame(frame)
    }
  }, [phone])

  // Read as well as reset: the effect above lands a render after the width changes, and
  // the bar must not spend that render away.
  return phone ? false : hidden
}
